import Image from "@/atoms/Image";
import { IS_LIVE } from "@/utils/constants";
import H2 from "@/components/atoms/text/H2";
import Para from "@/components/atoms/text/Para";
import Label from "@/components/atoms/text/Label";
import Theme from "@/components/Theme";
import Hero from "@/organisms/Hero";
import Slider from "@/components/organisms/Slider";
import AudioLayout from "@/organisms/AudioLayout";
import Discover from "@/organisms/Discover";
import ServiceCard from "@/components/molecules/CardLink";
import ServicePill from "@/components/molecules/PillLink";
import {
  ReceptionistOnCallIcon,
  ClockWithArrowIcon,
  AgentOnCallIcon,
  CalendarIcon,
  FilterIcon,
  LiveChatIcon,
} from "@/atoms/Icons";
import Trustpilot from "@/organisms/Trustpilot";

export default function Services() {
  const seoData = {
    indexing: true,
    title: "Grow Your Business With 24/7 Answering Services",
    description:
      "AnswerForce helps your business with 24/7 live answering, live chat, appointment setting, bilingual call handling, and more from our team of live virtual receptionists.",
    og: {
      title: "Grow Your Business With 24/7 Answering Services",
      description:
        "AnswerForce helps your business with 24/7 live answering, live chat, appointment setting, bilingual call handling, and more from our team of live virtual receptionists.",
      url: "https://www.answerforce.com/services",
      url_ca: "https://www.answerforce.com/ca/services",
      image:
        "https://assets.answerforce.com/answerforce/us/website/images/og/services-og.jpg",
    },
    twitter: {
      title: "Grow Your Business With 24/7 Answering Services",
      description:
        "AnswerForce helps your business with 24/7 live answering, live chat, appointment setting, bilingual call handling, and more from our team of live virtual receptionists.",
      url: "https://www.answerforce.com/services",
      image:
        "https://assets.answerforce.com/answerforce/us/website/images/og/services-og.jpg",
      image_alt: "AnswerForce industries page",
    },
  };

  const heroImgData = {
    fileName: "services-hero",
    alt: "Service providers",
  };

  const hero = (
    <Hero
      heroTitle="Services that scale<br class='desktop-only' /> your business<span class='dot'>.</span>"
      heroContent="Make your business 24/7 with people-powered services<br class='desktop-only' /> designed to grow along with your business."
      heroImgData={heroImgData}
      hasPricingCta
      hasTrustpilot
    />
  );

  const SliderData = [
    {
      hasMobileAppImg: true,
      title: "Tools",
      content:
        "Powerful tools to connect your prospects, clients, and team with your business.",
      list: [
        "Mobile app",
        "Live chat",
        "Business phone line",
        "Appointment scheduling",
      ],
      hasCta: true,
      cta: {
        href: "/tools",
        text: "View all tools",
      },
      image: {
        fileName: "tools-mobile-app",
        altName: "mobile app",
        isAlignBottom: true,
        desktopWidth: "500",
      },
    },
    {
      title: "Industries",
      content:
        "Contractor receptionists with tailored solutions for your business and industry.",
      list: ["HVAC", "Restoration", "Plumbing", "Contractors"],
      hasCta: true,
      cta: {
        href: "/industries",
        text: "View all industries",
      },
      image: {
        fileName: "technician-working-01",
        altName: "technician-working",
        isAlignBottom: false,
        desktopWidth: "500",
      },
    },
    {
      title: "Integrations",
      content:
        "Connect AnswerForce receptionists to your job management software, CRM tools, marketing platforms, and more.",
      list: ["ServiceTitan", "Housecall Pro", "Workiz", "Zapier"],
      hasCta: true,
      cta: {
        href: "/integrations",
        text: "View all integrations",
      },
      image: {
        fileName: "integrations-grid",
        altName: "integration icons",
        isAlignBottom: false,
        desktopWidth: "500",
      },
    },
  ];

  const serviceCardsData = [
    {
      icon: ReceptionistOnCallIcon,
      title: "24/7 call answering",
      content:
        "Extend your reputation with real people answering every call 24 hours a day.",
      href: "/answering-services",
    },
    {
      icon: ClockWithArrowIcon,
      title: "After-hours answering",
      content:
        "Emergencies can happen anytime. Be there for your customers day or night.",
      href: "/answering-services/after-hours-answering",
    },
    {
      icon: AgentOnCallIcon,
      title: "Virtual receptionists",
      content:
        "Professional receptionists ready to engage with your clients day and night.",
      href: "/virtual-receptionist",
    },
    {
      icon: CalendarIcon,
      title: "Appointment scheduling",
      content:
        "Estimates, work, and calls scheduled directly into your calendar.",
      href: "/answering-services/appointment-setting",
    },
    {
      icon: FilterIcon,
      title: "Lead qualification",
      content:
        "Focus on the job while every lead is captured, screened, and prioritized.",
      href: "/answering-services/lead-capture",
    },
    {
      icon: LiveChatIcon,
      title: "Live chat answering",
      content:
        "Build connections and capture leads through your website and Facebook 24/7.",
      href: "/live-chat",
    },
  ];

  const servicePillsData = [
    {
      text: "Bilingual answering service",
      href: "/answering-services/bilingual-answering",
    },
    {
      text: "Call centre solutions",
      href: "/call-center-solutions",
    },
    {
      text: "Order taking",
      href: "/services/order-taking",
    },
    {
      text: "Franchise answering",
      href: "/franchise",
    },
  ];

  return (
    <Theme mainClassName="main" hasSidebar hero={hero} seo={seoData}>
      <section className="bc-white pv-15-d pv-8">
        <div className="container">
          <Label size="lg" color="blue-light" className="ta-center bold mb-3">
            Services
          </Label>
          <H2 className="bold mb-8-d mb-7 ta-center">
            Powered by
            <br class="desktop-hide" /> people 24/7
            <span className="dot">.</span>
          </H2>
          <ServiceCard data={serviceCardsData} />
          <ServicePill data={servicePillsData} />
          <Image
            fileName="construction-worker"
            fileFormat="png"
            desktopWidth="100%"
            mobileWidth="100%"
            alt="construction worker"
            className="w-100 mv-8-d mv-6"
          />
          <div className="fx fx--c fx--r-d fx--cv-d fx--sb">
            <H2 className="bold w-50-d mr-10-d mb-reset-d mb-3">
              There for callers,
              <br class name="desktop-only" /> day or night
              <span className="dot">.</span>
            </H2>
            <div className="w-50-d">
              <Para desktopSize="lg" mobileSize="lg">
                Give callers a reliable response, whenever they reach out. Our
                after-hours answering service ensures your callers never go to
                voicemail.
              </Para>
            </div>
          </div>
        </div>
      </section>
      <Slider
        data={SliderData}
        bgColor="blue-light"
        variant="solutions"
        title="Plans that include more<span class='dot'>.</span>"
        subTitle="Get a plan that's built to give you more time and flexibility, support your clients,<br class='desktop-only' /> and work with your processes."
      />
      <AudioLayout
        title="Hear how <br class='desktop-only'>we <br class='phone-only'>sound"
        image={{
          fileName: "refer-friend-female-receptionist",
          altText: "Female receptionist",
          desktopWidth: 348,
          mobileWidth: 308,
        }}
      />
      <Trustpilot title="In our customers&#8217; words" variant="carousel" />
      <Discover
        bgColor="white"
        title="Begin your journey to 24/7 growth<span class='dot'>.</span>"
        color="black"
        cta="See Prices"
        href={
          IS_LIVE
            ? "https://www.answerforce.com/register"
            : "https://testing.answerforce.com/register"
        }
      />
    </Theme>
  );
}
